/**
 * Global sequential toast queue for react-toastify.
 *
 * Patches toast.info / success / warning / error / warn so multiple toasts
 * show one after another instead of stacking at the same time.
 *
 * Call enableSequentialToasts() once at app startup (App.jsx).
 */
import { toast } from "react-toastify";

const DEFAULT_TOAST_MS = 5000;
const TOAST_GAP_MS = 300;

const queue = [];
let busy = false;
let enabled = false;

const originals = {
	info: null,
	success: null,
	warning: null,
	error: null,
	warn: null,
	default: null,
};

const resolveAutoClose = (options = {}) => {
	if (options.autoClose === false) return false;
	if (typeof options.autoClose === "number") return options.autoClose;
	return DEFAULT_TOAST_MS;
};

const drain = () => {
	if (busy) return;
	const next = queue.shift();
	if (!next) return;

	busy = true;
	const { method, content, options } = next;
	const autoClose = resolveAutoClose(options);
	const userOnClose = options.onClose;
	const invoke = originals[method] || originals.default;

	invoke(content, {
		...options,
		autoClose,
		onClose: (...args) => {
			try {
				userOnClose?.(...args);
			} finally {
				busy = false;
				setTimeout(drain, TOAST_GAP_MS);
			}
		},
	});
};

const enqueue = (method, content, options = {}) => {
	queue.push({ method, content, options });
	drain();
	// react-toastify normally returns a toast id; queue is async so return undefined
	return undefined;
};

/**
 * Patch react-toastify toast helpers to use a sequential queue.
 * Safe to call multiple times (idempotent).
 */
export const enableSequentialToasts = () => {
	if (enabled) return;

	originals.info = toast.info.bind(toast);
	originals.success = toast.success.bind(toast);
	originals.warning = toast.warning.bind(toast);
	originals.error = toast.error.bind(toast);
	originals.warn = (toast.warn || toast.warning).bind(toast);
	originals.default = toast.bind(toast);

	toast.info = (content, options) => enqueue("info", content, options || {});
	toast.success = (content, options) => enqueue("success", content, options || {});
	toast.warning = (content, options) => enqueue("warning", content, options || {});
	toast.error = (content, options) => enqueue("error", content, options || {});
	toast.warn = (content, options) => enqueue("warn", content, options || {});

	enabled = true;
};

/** Bypass the queue and show immediately (rare escape hatch). */
export const toastImmediate = {
	info: (...args) => (originals.info || toast.info)(...args),
	success: (...args) => (originals.success || toast.success)(...args),
	warning: (...args) => (originals.warning || toast.warning)(...args),
	error: (...args) => (originals.error || toast.error)(...args),
};
